import { useParams } from "react-router-dom";
import {
  useGetAdminQuizSourceMaterialQuery,
  useGetAdminQuizSourceMaterialDownloadUrlLazyQuery,
  useUpdateAdminQuizSourceMaterialMutation,
  useGenerateAdminQuizzesMutation,
  useGetAdminQuizDifficultiesQuery
} from "../../../lib/apollo/graphql/quiz/quiz-generated";
import PageWrapper from "../../common/components/PageWrapper";
import DocumentDetail from "../components/DocumentDetail";
import { Document } from "../components/DocumentTable";

export default function DocumentDetailContainer() {
  const { documentId } = useParams<{ documentId: string }>();
  const { data, loading, refetch } = useGetAdminQuizSourceMaterialQuery({
    variables: { id: documentId || "" },
    skip: !documentId,
  });
  const { data: difficultiesData } = useGetAdminQuizDifficultiesQuery({
    variables: { includeInactive: false }
  });
  const [getDownloadUrl] = useGetAdminQuizSourceMaterialDownloadUrlLazyQuery();
  const [updateSourceMaterial] = useUpdateAdminQuizSourceMaterialMutation();
  const [generateQuizzes] = useGenerateAdminQuizzesMutation();

  // 활성화된 난이도 목록을 order 순으로 정렬
  const difficulties = difficultiesData?.difficulties
    ? [...difficultiesData.difficulties].sort((a, b) => a.order - b.order)
    : [];

  const document: Document | null = data?.sourceMaterial
    ? {
        documentId: data.sourceMaterial.id,
        title: data.sourceMaterial.title,
        fileName: data.sourceMaterial.title, // API에서 filename 필드가 없으므로 title 사용
        contentType: data.sourceMaterial.contentType,
        creator: data.sourceMaterial.createdBy || "-",
        createdAt: data.sourceMaterial.createdAt,
        updatedAt: data.sourceMaterial.updatedAt,
      }
    : null;

  // Transform generation history
  const generationHistory = data?.sourceMaterial?.generationHistory || [];

  const handleSaveTitle = async (docId: string, newTitle: string) => {
    try {
      await updateSourceMaterial({
        variables: {
          id: docId,
          input: {
            title: newTitle,
          },
        },
      });
      alert("제목이 수정되었습니다.");
    } catch (error) {
      console.error("Title update error:", error);
      alert("제목 수정에 실패했습니다.");
    }
  };

  const handleDownload = async (docId: string) => {
    try {
      const { data } = await getDownloadUrl({ variables: { id: docId } });
      const url = data?.getSourceMaterialDownloadUrl;

      if (!url) {
        alert("파일에 문제가 있습니다. 데이터 관리자에게 문의하세요.");
        return;
      }

      // Open URL in new tab for download
      window.open(url, "_blank");
    } catch (error) {
      console.error("Download URL fetch error:", error);
      alert("파일에 문제가 있습니다. 데이터 관리자에게 문의하세요.");
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleGenerateQuizzes = async (
    mcqCount: number,
    oxCount: number,
    shortCount: number,
    difficulty: string
  ) => {
    // 모든 개수가 0인지 확인
    if (mcqCount === 0 && oxCount === 0 && shortCount === 0) {
      alert("생성할 유형 개수를 설정하세요.");
      return;
    }

    // 난이도 매핑
    const difficultyMap: { [key: string]: string } = {
      "쉬움": "EASY",
      "적당함": "MEDIUM",
      "어려움": "HARD"
    };

    const difficultyValue = difficultyMap[difficulty] || "MEDIUM";

    // 확인 메시지 생성
    const parts = [];
    if (mcqCount > 0) parts.push(`객관식 ${mcqCount}개`);
    if (oxCount > 0) parts.push(`O/X ${oxCount}개`);
    if (shortCount > 0) parts.push(`단답형 ${shortCount}개`);

    const message = `${parts.join(", ")}를 ${difficulty} 난이도로 생성하시겠습니까?`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      const { data } = await generateQuizzes({
        variables: {
          input: {
            sourceMaterialId: documentId || "",
            mcqCount,
            oxCount,
            shortCount,
            difficulty: difficultyValue as any,
          },
        },
      });

      if (data?.generateQuizzes) {
        alert(`퀴즈 생성이 시작되었습니다.\n3~4분 내외의 시간이 소요될 수 있습니다.`);
        // Refetch document to update generation history
        refetch();
      }
    } catch (error) {
      console.error("Quiz generation error:", error);
      alert("퀴즈 생성 요청에 실패했습니다.");
    }
  };

  if (loading) {
    return (
      <PageWrapper className="overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </PageWrapper>
    );
  }

  if (!document) {
    return (
      <PageWrapper className="overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">문서를 찾을 수 없습니다.</div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper className="overflow-y-auto">
      <DocumentDetail
        document={document}
        onSaveTitle={handleSaveTitle}
        onDownload={handleDownload}
        onGenerateQuizzes={handleGenerateQuizzes}
        onRefresh={handleRefresh}
        difficulties={difficulties}
        generationHistory={generationHistory}
      />
    </PageWrapper>
  );
}